import {
  AccountDetails,
  AppPlan,
  Card,
  FileField,
  PlanLimit,
  RequestFile,
  RequestParticipant,
  SignRequest,
} from "models/interfaces";
import { createContext, Dispatch, SetStateAction } from "react";

interface CustomProps {
  signRequest: SignRequest;
  setSignRequest: Dispatch<SetStateAction<SignRequest>>;
  selectedParticipant: RequestParticipant;
  setSelectedParticipant: (selectedParticipant: RequestParticipant) => void;
  selectedField: FileField;
  setSelectedField: (selectedField: FileField) => void;
  selectedFile: RequestFile;
  setSelectedFile: (selectedFile: RequestFile) => void;
  isRequest: boolean;
  setIsRequest: (selectedParticipant: boolean) => void;
}

interface AccountContextProps {
  planLimits: Map<string, PlanLimit>;
  setPlanLimits: (planLimits: Map<string, PlanLimit>) => void;
  planStatus: string;
  setPlanStatus: (planStatus: string) => void;
  cards: Card[];
  setCards: (cards: Card[]) => void;
  currentPlan: AppPlan;
  setCurrentPlan: (currentPlan: AppPlan) => void;
  accountDetails: AccountDetails;
  setAccountDetails: (accountDetails: AccountDetails) => void;
}

export const initialSignRequest: SignRequest = {
  id: "",
  signers: [],
  files: [],
  expirationDays: 180,
  emailSubject: "",
  emailMessage: "",
  requestedBy: { name: "", email: "" },
  selfSigned: false,
  signerOrderRequired: false,
  signedBy: "",
  status: "",
  templateName: "",
  imagePath: "",
  scheduleTime: "",
  approval: {
    approvalNeeded: false,
    orderNeeded: false,
    reviewers: [],
  },
  reminders: {
    daysBeforeFirstReminder: 5,
    daysBetweenReminders: 3,
  },
  needReminders: false,
  signRequestConfig: {
    attachDocumentToMailOnCompletion: true,
    allowDocumentDownload: true,
    sendNotificationOnCompletion: true,
    attachSingleCertificate: true,
    allowSignatureDelegation: true,
  },
  currentSigners: [],
  requestToken: "",
  autoGeneratedId: 0,
  templateId: null,
};

export const initialSelectedParticipant: RequestParticipant = {
  id: 0,
  name: "",
  email: "",
  order: 0,
  color: "",
  status: "",
  cc: false,
  delayDays: 0,
  delayHours: 0,
  scheduleTime: "",
  requestRole: "",
  companyName: "",
  title: "",
  group: "",
};

export const initialSelectedFile: RequestFile = {
  unsignedDoc: "",
  unsignedDocUrl: "",
  signedDoc: "",
  fields: [],
  pageCount: 0,
  pageUrls: {},
  dimensions: {},
};

export const initialSelectedField: FileField = {
  id: 0,
  name: "",
  type: "",
  value: "",
  frameX: 0,
  frameY: 0,
  x: 0,
  y: 0,
  w: 0,
  h: 0,
  width: 0,
  height: 0,
  color: "",
  page: 0,
  signerIndex: 0,
  maxLength: 0,
  required: false,
  hidden: false,
  readOnly: false,
  tabLabel: "",
  label: "",
  toolTip: "",
  validationPattern: "none",
  validationMessage: "",
  placeholder: "",
  multiLine: false,
  styles: {
    fontFamily: "none",
    color: "",
    size: 16,
    fontWeight: {
      bold: false,
      italic: false,
      underline: false,
    },
  },
  group: [],
  top: 0,
  left: 0,
};

export const GeneralContext = createContext({} as CustomProps);

export const AccountContext = createContext({} as AccountContextProps);
